import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Modal from '../Modal';
import SettingDetails, { SettingDetailsContext } from '../SettingDetails';

/**
 * A modal whose body is a list of settings rows — the panels off View's plugin titles.
 *
 * A row can carry more than its one-line description fits. Asking for that long copy pages this
 * modal to it in place: the row's label takes over the header, a back arrow appears, and the
 * footer's Done goes away since there is nothing to commit on a page of prose. It stays inside the
 * *same* Modal — returning a second one from another branch would read as a swap but reconcile as
 * one, and stacking a dialog on a dialog puts two backdrops on screen.
 *
 * Rows reach this through SettingDetailsContext, so nothing in between has to pass a prop down.
 */
const SettingsModal = ({ isOpen, title, children, footer = null, className = '', ...modalProps }) => {
  const [details, setDetails] = useState(null);
  const bodyRef = useRef(null);

  // Reopening lands on the list, not on whatever page was last read
  useEffect(() => {
    if (!isOpen) setDetails(null);
  }, [isOpen]);

  // Paging swaps what is inside the scrolling element but not the element itself, so without this
  // each page inherits the offset the last one was left at: a long details page opens already part
  // way down, and coming back from the foot of one drops the list at its own bottom. Both are the
  // page arriving somewhere other than its start. Before paint, so the new page is never seen in
  // the old position.
  useLayoutEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [details]);

  return (
    <SettingDetailsContext.Provider value={setDetails}>
      <Modal
        {...modalProps}
        isOpen={isOpen}
        bodyRef={bodyRef}
        title={details ? details.title : title}
        onBack={details ? () => setDetails(null) : null}
        footer={details ? null : footer}
        className={classNames('settings-modal', className)}
      >
        {details ? <SettingDetails blocks={details.blocks} /> : children}
      </Modal>
    </SettingDetailsContext.Provider>
  );
};

SettingsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  footer: PropTypes.node,
  className: PropTypes.string,
};

export default SettingsModal;
